/**
 * A track's own title, read from its MP3 tags (ID3v2 `TIT2`, v2.2's `TT2`) — the data has no title for a set's sample,
 * only its file, and the player says what it plays (the user, 2026-11-02: "if it's a sample, the track's name").
 * Reads the file's first 64 kB (a Range request) and nothing else; any failure — no tags, a server that refuses a
 * cross-origin read — is null, and the caller says "Sample".
 */
import { useEffect, useState } from 'react';
import { soundcloudInfoOf, soundcloudPageOf } from './sample.js';

const HEAD_BYTES = 65_536;
const cache = new Map<string, Promise<string | null>>();

/** The title in an ID3v2 head, or null. Exported for the test. */
export function titleFromId3(bytes: Uint8Array): string | null {
  if (bytes.length < 10 || bytes[0] !== 0x49 || bytes[1] !== 0x44 || bytes[2] !== 0x33) return null; // "ID3"
  const version = bytes[3]!;
  const syncsafe = (i: number) => ((bytes[i]! & 0x7f) << 21) | ((bytes[i + 1]! & 0x7f) << 14) | ((bytes[i + 2]! & 0x7f) << 7) | (bytes[i + 3]! & 0x7f);
  const end = Math.min(bytes.length, 10 + syncsafe(6));
  let at = 10;
  while (at + (version === 2 ? 6 : 10) <= end) {
    if (version === 2) {
      const id = String.fromCharCode(bytes[at]!, bytes[at + 1]!, bytes[at + 2]!);
      const size = (bytes[at + 3]! << 16) | (bytes[at + 4]! << 8) | bytes[at + 5]!;
      if (!size || id === '\0\0\0') return null;
      if (id === 'TT2') return text(bytes.subarray(at + 6, at + 6 + size));
      at += 6 + size;
    } else {
      const id = String.fromCharCode(bytes[at]!, bytes[at + 1]!, bytes[at + 2]!, bytes[at + 3]!);
      const size = version >= 4 ? syncsafe(at + 4)
        : ((bytes[at + 4]! << 24) | (bytes[at + 5]! << 16) | (bytes[at + 6]! << 8) | bytes[at + 7]!) >>> 0;
      if (!size || id === '\0\0\0\0') return null;
      if (id === 'TIT2') return text(bytes.subarray(at + 10, at + 10 + size));
      at += 10 + size;
    }
  }
  return null;
}

/** A text frame: its encoding byte, then the words. */
function text(frame: Uint8Array): string | null {
  if (frame.length < 2) return null;
  const body = frame.subarray(1);
  const label = frame[0] === 0 ? 'latin1' : frame[0] === 1 ? 'utf-16' : frame[0] === 2 ? 'utf-16be' : 'utf-8';
  try {
    const words = new TextDecoder(label).decode(body).replace(/\0+$/g, '').replace(/\0/g, ' ').trim();
    return words || null;
  } catch { return null; }
}

/**
 * A sample's track: its title, and — for a SoundCloud sample — the track's page on SoundCloud, which the artist panel
 * and the player both name as its source (the user, 2026-11-02: "the name of the track … and the source should be
 * SoundCloud on both"). Asked once per address and shared by every screen.
 */
export interface TrackInfo {
  title: string | null;
  soundcloud: string | null;
  /** The whole track, as HLS — a browser without HLS of its own plays it through hls.js (`lib/lazy/hlsAudio.ts`). */
  hls?: boolean;
}
const infos = new Map<string, Promise<TrackInfo>>();

export function trackInfo(src: string): Promise<TrackInfo> {
  let found = infos.get(src);
  if (!found) {
    // A SoundCloud sample (lib/sample.ts): its title is SoundCloud's, which the API answers — the preview has no tags.
    const info = soundcloudInfoOf(src);
    found = info
      ? fetch(info)
        .then(async (r) => {
          if (!r.ok) return { title: null, soundcloud: soundcloudPageOf(src) };
          const j = (await r.json()) as { title?: string | null; url?: string | null; hls?: boolean };
          return { title: j.title ?? null, soundcloud: j.url ?? soundcloudPageOf(src), hls: j.hls === true };
        })
        .catch(() => ({ title: null, soundcloud: soundcloudPageOf(src) }))
      : trackTitle(src).then((title) => ({ title, soundcloud: null }));
    infos.set(src, found);
  }
  return found;
}

/** The track a sample plays, for a screen: null until known. */
export function useTrackInfo(src: string | null | undefined): TrackInfo | null {
  const [info, setInfo] = useState<TrackInfo | null>(null);
  useEffect(() => {
    setInfo(null);
    if (!src) return undefined;
    let live = true;
    void trackInfo(src).then((i) => { if (live) setInfo(i); });
    return () => { live = false; };
  }, [src]);
  return info;
}

export function trackTitle(src: string): Promise<string | null> {
  if (soundcloudInfoOf(src)) return trackInfo(src).then((i) => i.title);
  let found = cache.get(src);
  if (!found) {
    found = fetch(src, { headers: { Range: `bytes=0-${HEAD_BYTES - 1}` } })
      .then(async (r) => (r.ok ? titleFromId3(new Uint8Array(await r.arrayBuffer()).subarray(0, HEAD_BYTES)) : null))
      .catch(() => null);
    cache.set(src, found);
  }
  return found;
}
